import React, {useCallback, useEffect, useRef, useState} from 'react';
import {AGENT_SESSION_EXPIRED, agentApi} from './api';
import {usePresence} from './hooks/usePresence';
import {useRinging} from './hooks/useRinging';
import {useAgentSocket} from './hooks/useAgentSocket';
import {useProperties} from './hooks/useProperties';
import {BottomNav, Sidebar} from './components/ui';
import {IncomingCallModal} from './components/IncomingCallModal';
import {VideoCallScreen} from './components/VideoCallScreen';
import {LoginPage} from './pages/LoginPage';
import {HomePage} from './pages/HomePage';
import {CallHistoryPage} from './pages/CallHistoryPage';
import {AvailabilityPage} from './pages/AvailabilityPage';
import {UpcomingCallsPage} from './pages/UpcomingCallsPage';
import {SettingsPage} from './pages/SettingsPage';
import './styles/agent.css';

const TOKEN_KEY = 'agent_token';
const DATA_KEY = 'agent_data';

const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
  remove: (k) => { try { localStorage.removeItem(k); } catch { /* private mode */ } },
};

function Workspace({agent, onLogout, onAgentUpdate}) {
  const [page, setPage] = useState('dashboard');
  const [activeCall, setActiveCall] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const [dismissed, setDismissed] = useState(() => new Set());
  const [refreshKey, setRefreshKey] = useState(0);
  const agentId = agent.agentId || agent.id;

  const presence = usePresence(agentId);
  const ringing = useRinging(presence.online && !presence.busy && !activeCall);
  const {wsStatus} = useAgentSocket(agentId, ringing.onMessage);
  const {findPropertyById, findProperty} = useProperties();
  const visible = ringing.calls.filter((c) => !dismissed.has(c.requestId));
  const ringer = visible[0];

  useEffect(() => {
    if (!ringing.notice) return undefined;
    setRefreshKey((k) => k + 1);  // a call that closed (taken or missed) changes the numbers and Missed calls
    const t = setTimeout(ringing.clearNotice, 12000);
    return () => clearTimeout(t);
  }, [ringing.notice, ringing.clearNotice]);

  const accept = useCallback(async (caller) => {
    setAccepting(true);
    setAcceptError('');
    try {
      const res = await agentApi.accept(caller.requestId);
      ringing.remove(caller.requestId);
      presence.markBusy(true);
      setActiveCall({...caller, roomName: res.roomName || caller.roomName, sessionId: res.sessionId});
    } catch (e) {
      ringing.remove(caller.requestId);
      setAcceptError(e.status === 409 ? e.message : `Couldn't connect: ${e.message}`);
    } finally {
      setAccepting(false);
    }
  }, [ringing, presence]);

  const endCall = useCallback(async () => {
    const call = activeCall;
    setActiveCall(null);
    presence.markBusy(false);
    if (call?.sessionId) await agentApi.end(call.sessionId).catch(() => {});
    setRefreshKey((k) => k + 1);
  }, [activeCall, presence]);

  const endRef = useRef(null);
  endRef.current = activeCall?.sessionId;
  useEffect(() => {
    const leave = () => { if (endRef.current) agentApi.end(endRef.current, {keepalive: true}).catch(() => {}); };
    window.addEventListener('pagehide', leave);
    return () => window.removeEventListener('pagehide', leave);
  }, []);

  if (activeCall) {
    return (
        <VideoCallScreen
            caller={activeCall}
            agent={agent}
            property={findPropertyById(activeCall.propertyId) || findProperty(activeCall.property)}
            onEnd={endCall}
        />
    );
  }

  const pages = {
    dashboard: <HomePage agent={agent} presence={presence} waiting={ringing.calls} onAccept={accept} accepting={accepting}
                         onOpenUpcoming={() => setPage('upcoming-calls')} refreshKey={refreshKey}/>,
    'call-history': <CallHistoryPage agentId={agentId}/>,
    availability: <AvailabilityPage agentId={agentId}/>,
    'upcoming-calls': <UpcomingCallsPage agentId={agentId}
                                         onJoinCall={(c) => { presence.markBusy(true); setActiveCall(c); }}/>,
    settings: <SettingsPage agent={agent} onAgentUpdate={onAgentUpdate} onLogout={onLogout}/>,
  };

  return (
      <>
        <div className="app-layout">
          <Sidebar active={page} onNav={setPage} onLogout={async () => { await presence.setOnline(false); onLogout(); }}
                   wsStatus={wsStatus}/>
          <main className="main-content" id="main-content">
            {(ringing.notice || acceptError) && (
                <div className="ag-toast" role="status">
                  {acceptError || ringing.notice}
                  <button type="button" onClick={() => { setAcceptError(''); ringing.clearNotice(); }} aria-label="Dismiss">✕</button>
                </div>
            )}
            {presence.online && wsStatus !== 'connected' && (
                <div className="ag-toast ag-toast--warn" role="alert">
                  Reconnecting to live calls… new calls may not ring until this clears.
                </div>
            )}
            {pages[page]}
          </main>
        </div>
        <BottomNav active={page} onNav={setPage} wsStatus={wsStatus}/>

        {ringer && (
            <IncomingCallModal
                key={ringer.requestId}
                caller={ringer}
                waitingCount={visible.length}
                accepting={accepting}
                error={acceptError}
                onAccept={() => accept(ringer)}
                onDecline={() => setDismissed((s) => new Set(s).add(ringer.requestId))}
            />
        )}
      </>
  );
}

/**
 * The live-tour agent console (/agent-admin): sign-in, presence (online/busy with a heartbeat), ringing for every
 * waiting customer, the call itself, missed calls, bookings, working hours and settings. Backend:
 * app/modules/live (router_agent.py, calls.py, presence.py).
 */
export default function AgentApp() {
  const [agent, setAgent] = useState(null);
  const [restoring, setRestoring] = useState(!!store.get(TOKEN_KEY));

  const logout = useCallback(() => {
    store.remove(TOKEN_KEY);
    store.remove(DATA_KEY);
    setAgent(null);
  }, []);

  useEffect(() => {
    if (!store.get(TOKEN_KEY)) return;
    agentApi.profile()
        .then((data) => setAgent({...data, token: store.get(TOKEN_KEY), _localPhoto: store.get(`agent_photo_${data.agentId}`)}))
        .catch(logout)
        .finally(() => setRestoring(false));
  }, [logout]);

  useEffect(() => {
    window.addEventListener(AGENT_SESSION_EXPIRED, logout);
    return () => window.removeEventListener(AGENT_SESSION_EXPIRED, logout);
  }, [logout]);

  const login = useCallback((data) => {
    store.set(TOKEN_KEY, data.token);
    store.set(DATA_KEY, JSON.stringify(data));
    setAgent({...data, _localPhoto: store.get(`agent_photo_${data.agentId}`)});
  }, []);

  const update = useCallback((updated) => {
    setAgent(updated);
    store.set(DATA_KEY, JSON.stringify(updated));
  }, []);

  if (restoring) return <div className="ag-boot"><span className="login-spinner"/></div>;
  if (!agent) return <LoginPage onLogin={login}/>;
  return <Workspace agent={agent} onLogout={logout} onAgentUpdate={update}/>;
}
